import { useEffect, useState } from "react";
import { motion, useSpring, useMotionValueEvent } from "framer-motion";
import axios from "axios";
import { Info, Phone } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  API,
  DEFAULT_PRICING,
  PHONE_DISPLAY,
  PHONE_TEL,
  formatLei,
} from "@/lib/api";

function AnimatedNumber({ value }) {
  const spring = useSpring(value, { stiffness: 80, damping: 22 });
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    spring.set(value);
  }, [value, spring]);
  useMotionValueEvent(spring, "change", (v) => setDisplay(Math.round(v)));
  return <span>{formatLei(display)}</span>;
}

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function CalculatorSection({ onOpenQuote, defaultLocality }) {
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [depth, setDepth] = useState(60);
  const [casingId, setCasingId] = useState("d125");
  const [locality, setLocality] = useState(
    defaultLocality || DEFAULT_PRICING.localities[0].name,
  );

  useEffect(() => {
    axios
      .get(`${API}/pricing`)
      .then((r) => r.data && r.data.casings && setPricing(r.data))
      .catch(() => {});
  }, []);

  const casing = pricing.casings.find((c) => c.id === casingId) || pricing.casings[0];
  const rate = pricing.base_rate_per_meter + casing.extra_per_meter;
  const loc = pricing.localities.find((l) => l.name === locality);
  const transport = loc ? loc.transport : 0;
  const total = depth * rate + transport;
  const min = Math.round((total * 0.95) / 50) * 50;
  const max = Math.round((total * 1.15) / 50) * 50;
  const mid = Math.round((min + max) / 2);

  const fill = ((depth - pricing.min_depth) / (pricing.max_depth - pricing.min_depth)) * 100;
  const groups = pricing.localities.reduce((acc, l) => {
    (acc[l.group] = acc[l.group] || []).push(l);
    return acc;
  }, {});

  return (
    <section id="calculator" className="scroll-mt-16 bg-hydro-ice py-24 text-slate-900 md:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-hydro-blue sm:text-xs">
            Calculator de preț
          </p>
          <h2 className="mt-4 max-w-2xl font-display text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl md:text-6xl">
            Află în 30 de secunde cât costă puțul tău.
          </h2>
          <p className="mt-4 max-w-xl text-base text-slate-600">
            Alege adâncimea și localitatea — estimarea se recalculează pe loc.
          </p>
        </motion.div>

        <div className="mt-12 grid items-start gap-6 lg:grid-cols-[1.15fr_1fr]">
          {/* controls */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="rounded-3xl border border-slate-200 bg-white p-7 shadow-[0_20px_60px_rgba(11,25,44,0.08)] md:p-9"
          >
            <div className="flex items-end justify-between gap-4">
              <label htmlFor="depth-slider" className="text-sm font-bold text-slate-500">
                Adâncime foraj
              </label>
              <span data-testid="calc-depth-value" className="font-mono text-3xl font-bold text-hydro-blue">
                {depth} m
              </span>
            </div>
            <input
              id="depth-slider"
              type="range"
              min={pricing.min_depth}
              max={pricing.max_depth}
              step={pricing.step}
              value={depth}
              data-testid="calc-depth-slider"
              onChange={(e) => setDepth(Number(e.target.value))}
              className="depth-slider mt-5"
              style={{ "--fill": `${fill}%` }}
              aria-label="Adâncime foraj în metri"
            />
            <div className="mt-2 flex justify-between font-mono text-[11px] text-slate-400">
              <span>{pricing.min_depth} m</span>
              <span>{pricing.max_depth} m</span>
            </div>

            <div className="mt-8">
              <p className="text-sm font-bold text-slate-500">Țeavă PVC albastră cu filet</p>
              <div className="mt-3 flex flex-wrap gap-2.5">
                {pricing.casings.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`calc-casing-${c.id}`}
                    aria-pressed={casingId === c.id}
                    onClick={() => setCasingId(c.id)}
                    className={`rounded-xl border px-4 py-2.5 text-left transition-all ${
                      casingId === c.id
                        ? "border-hydro-blue bg-hydro-blue text-white shadow-lg shadow-blue-900/20"
                        : "border-slate-300 bg-white text-slate-700 hover:border-hydro-blue"
                    }`}
                  >
                    <span className="block text-sm font-extrabold">{c.label}</span>
                    <span className={`block text-[11px] ${casingId === c.id ? "text-white/70" : "text-slate-400"}`}>
                      {c.detail}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <p className="text-sm font-bold text-slate-500">Localitate</p>
              <Select value={locality} onValueChange={setLocality}>
                <SelectTrigger
                  data-testid="calc-locality-select"
                  className="mt-3 h-12 w-full rounded-xl border-slate-300 text-base font-semibold"
                >
                  <SelectValue placeholder="Alege localitatea" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(groups).map(([g, items]) => (
                    <SelectGroup key={g}>
                      <SelectLabel>{g}</SelectLabel>
                      {items.map((l) => (
                        <SelectItem key={l.name} value={l.name} data-testid={`calc-locality-option-${l.name}`}>
                          {l.name}
                          {l.transport > 0 && (
                            <span className="ml-2 text-xs font-normal text-slate-400">
                              deplasare {formatLei(l.transport)} lei
                            </span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mt-8 flex items-start gap-2.5 rounded-xl bg-slate-50 p-4 text-[13px] leading-relaxed text-slate-500">
              <Info size={16} className="mt-0.5 shrink-0 text-hydro-blue" />
              <p>
                Preț orientativ, cu forajul, țeava cu filet și filtrele incluse. Prețul ferm
                îl confirmăm după vizita la teren.
              </p>
            </div>
          </motion.div>

          {/* estimate */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="rounded-3xl bg-hydro-navy p-8 text-white shadow-[0_30px_80px_rgba(11,25,44,0.35)] lg:sticky lg:top-24 md:p-10"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-hydro-cyan">
              Estimare orientativă
            </p>
            <div
              data-testid="calc-estimate-value"
              className="mt-4 font-display text-6xl font-black tracking-tight md:text-7xl"
            >
              <AnimatedNumber value={mid} />
              <span className="text-2xl font-bold text-white/50"> lei</span>
            </div>
            <p className="mt-2 text-sm text-white/55" data-testid="calc-estimate-range">
              între {formatLei(min)} și {formatLei(max)} lei, în funcție de teren
            </p>

            <div className="mt-8 space-y-3 border-t border-white/10 pt-6 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-white/60">
                  Foraj + țeavă {casing.label} · {depth} m × {rate} lei/m
                </span>
                <span className="shrink-0 font-mono font-semibold">{formatLei(depth * rate)} lei</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-white/60">Deplasare · {locality}</span>
                <span className="shrink-0 font-mono font-semibold" data-testid="calc-transport-value">
                  {transport === 0 ? "inclusă" : `${formatLei(transport)} lei`}
                </span>
              </div>
            </div>

            <button
              type="button"
              data-testid="calc-open-quote-button"
              onClick={() =>
                onOpenQuote({
                  depth,
                  casingId: casing.id,
                  casingLabel: casing.label,
                  locality,
                  estimate_min: min,
                  estimate_max: max,
                })
              }
              className="mt-9 w-full rounded-full bg-hydro-cyan py-4 text-base font-extrabold text-hydro-navy transition-all hover:bg-white hover:shadow-[0_10px_40px_rgba(0,180,216,0.35)]"
            >
              Solicită ofertă fermă
            </button>
            <a
              href={PHONE_TEL}
              data-testid="calc-call-link"
              className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-white/60 transition-colors hover:text-hydro-cyan"
            >
              <Phone size={14} />
              sau sună direct la {PHONE_DISPLAY}
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
